/**
 * Suppression List Page
 * Manages global email and SMS suppression for campaigns
 */

import { useState } from "react";
import { useDebounce } from "../hooks/useDebounce";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getSuppressedContacts,
  getSuppressionStats,
  unsuppressContact,
  bulkSuppressByEmail,
  searchSuppressedContacts,
  type SuppressedContact,
} from "../services/suppression.service";
import {
  NoSymbolIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XMarkIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { useToast } from "../components/ui/Toast";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Button from "../components/common/Button";

type SuppressionType = "email" | "sms" | "both";

export default function SuppressionList() {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const [filterType, setFilterType] = useState<SuppressionType>("email");
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [showAddModal, setShowAddModal] = useState(false);
  const [bulkEmails, setBulkEmails] = useState("");
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(
    new Set(),
  );

  // Fetch suppression stats
  const { data: stats } = useQuery({
    queryKey: ["suppression-stats"],
    queryFn: getSuppressionStats,
  });

  // Fetch suppressed contacts
  const {
    data: contacts = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["suppressed-contacts", filterType, debouncedSearch],
    queryFn: () =>
      debouncedSearch
        ? searchSuppressedContacts(debouncedSearch, filterType)
        : getSuppressedContacts(filterType),
  });

  // Unsuppress mutation
  const unsuppressMutation = useMutation({
    mutationFn: ({ id, type }: { id: string; type: SuppressionType }) =>
      unsuppressContact(id, type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["suppressed-contacts"] });
      queryClient.invalidateQueries({ queryKey: ["suppression-stats"] });
      showToast("success", "Contact removed from suppression list");
    },
    onError: (error: Error) => {
      showToast("error", error.message || "Failed to unsuppress contact");
    },
  });

  // Bulk suppress mutation
  const bulkSuppressMutation = useMutation({
    mutationFn: (emails: string[]) => bulkSuppressByEmail(emails, filterType),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["suppressed-contacts"] });
      queryClient.invalidateQueries({ queryKey: ["suppression-stats"] });
      setShowAddModal(false);
      setBulkEmails("");

      if (result.notFound.length > 0) {
        showToast(
          "warning",
          `Suppressed ${result.suppressed} contacts. ${result.notFound.length} emails not found in database.`,
        );
      } else {
        showToast("success", `Suppressed ${result.suppressed} contacts`);
      }
    },
    onError: (error: Error) => {
      showToast("error", error.message || "Failed to suppress contacts");
    },
  });

  const handleBulkSuppress = () => {
    const emails = bulkEmails
      .split(/[\n,;]/)
      .map((e) => e.trim())
      .filter((e) => e.length > 0);

    if (emails.length === 0) {
      showToast("error", "Please enter at least one email address");
      return;
    }

    bulkSuppressMutation.mutate(emails);
  };

  const handleSelectAll = () => {
    if (selectedContacts.size === contacts.length) {
      setSelectedContacts(new Set());
    } else {
      setSelectedContacts(new Set(contacts.map((c) => c.id)));
    }
  };

  const handleUnsuppressSelected = async () => {
    if (selectedContacts.size === 0) {
      return;
    }

    const promises = Array.from(selectedContacts).map((id) =>
      unsuppressContact(id, filterType),
    );

    try {
      await Promise.all(promises);
      queryClient.invalidateQueries({ queryKey: ["suppressed-contacts"] });
      queryClient.invalidateQueries({ queryKey: ["suppression-stats"] });
      setSelectedContacts(new Set());
      showToast("success", `Unsuppressed ${selectedContacts.size} contacts`);
    } catch (error) {
      showToast("error", "Failed to unsuppress some contacts");
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) {
      return "-";
    }
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Suppression List</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage contacts who have opted out of email or SMS communications
          </p>
        </div>
        <Button
          variant="primary"
          icon={PlusIcon}
          onClick={() => setShowAddModal(true)}
        >
          Add to Suppression
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <EnvelopeIcon className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {stats.total_contacts.toLocaleString()}
                </div>
                <div className="text-sm text-gray-500">Total Contacts</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 rounded-lg">
                <NoSymbolIcon className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600">
                  {stats.email_suppressed.toLocaleString()}
                </div>
                <div className="text-sm text-gray-500">Email Suppressed</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-50 rounded-lg">
                <DevicePhoneMobileIcon className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-600">
                  {stats.sms_suppressed.toLocaleString()}
                </div>
                <div className="text-sm text-gray-500">SMS Suppressed</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <CheckCircleIcon className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">
                  {(
                    ((stats.total_contacts - stats.email_suppressed) /
                      stats.total_contacts) *
                    100
                  ).toFixed(1)}
                  %
                </div>
                <div className="text-sm text-gray-500">Deliverable Rate</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          {/* Type Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Show:</span>
            <div className="flex rounded-lg border border-gray-300 overflow-hidden">
              {[
                { value: "email", label: "Email", icon: EnvelopeIcon },
                { value: "sms", label: "SMS", icon: DevicePhoneMobileIcon },
                { value: "both", label: "All", icon: NoSymbolIcon },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setFilterType(option.value as SuppressionType)}
                  className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors ${
                    filterType === option.value
                      ? "bg-primary-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <option.icon className="h-4 w-4" />
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Search */}
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by email or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Refresh */}
          <button
            onClick={() => refetch()}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            title="Refresh"
          >
            <ArrowPathIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Bulk Actions */}
        {selectedContacts.size > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200 flex items-center gap-4">
            <span className="text-sm text-gray-600">
              {selectedContacts.size} selected
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleUnsuppressSelected}
            >
              Remove from Suppression
            </Button>
            <button
              onClick={() => setSelectedContacts(new Set())}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear selection
            </button>
          </div>
        )}
      </div>

      {/* Contacts Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-12">
            <NoSymbolIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              No suppressed contacts
            </h3>
            <p className="text-gray-500">
              {searchQuery
                ? "No contacts match your search"
                : "Contacts who opt out will appear here"}
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedContacts.size === contacts.length}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SMS Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Suppressed Date
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contacts.map((contact: SuppressedContact) => (
                <tr key={contact.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedContacts.has(contact.id)}
                      onChange={(e) => {
                        const newSelected = new Set(selectedContacts);
                        if (e.target.checked) {
                          newSelected.add(contact.id);
                        } else {
                          newSelected.delete(contact.id);
                        }
                        setSelectedContacts(newSelected);
                      }}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">
                      {contact.first_name || contact.last_name
                        ? `${contact.first_name || ""} ${contact.last_name || ""}`.trim()
                        : "Unknown"}
                    </div>
                    <div className="text-sm text-gray-500">{contact.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    {contact.email_opt_in === false ||
                    contact.email_opt_out_at ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                        <NoSymbolIcon className="h-3 w-3" />
                        Suppressed
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                        <CheckCircleIcon className="h-3 w-3" />
                        Active
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {contact.sms_opt_in === false || contact.sms_opt_out_at ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                        <NoSymbolIcon className="h-3 w-3" />
                        Suppressed
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                        <CheckCircleIcon className="h-3 w-3" />
                        Active
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {formatDate(
                      contact.email_opt_out_at || contact.sms_opt_out_at,
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() =>
                        unsuppressMutation.mutate({
                          id: contact.id,
                          type: filterType,
                        })
                      }
                      disabled={unsuppressMutation.isPending}
                      className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-800 mb-2">
          About Suppression Lists
        </h4>
        <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
          <li>Suppressed contacts will not receive email or SMS campaigns</li>
          <li>
            Contacts can be suppressed manually or when they click unsubscribe
          </li>
          <li>
            Removing a contact from suppression will allow them to receive
            communications again
          </li>
          <li>
            Always respect unsubscribe requests to comply with CAN-SPAM and TCPA
            regulations
          </li>
        </ul>
      </div>

      {/* Add Suppression Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Add to Suppression List
              </h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Addresses
                </label>
                <textarea
                  value={bulkEmails}
                  onChange={(e) => setBulkEmails(e.target.value)}
                  placeholder="Enter email addresses (one per line, or comma-separated)"
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Only existing contacts will be suppressed. Unknown emails will
                  be skipped.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Suppression Type
                </label>
                <select
                  value={filterType}
                  onChange={(e) =>
                    setFilterType(e.target.value as SuppressionType)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="email">Email only</option>
                  <option value="sms">SMS only</option>
                  <option value="both">Both Email and SMS</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <Button
                variant="secondary"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleBulkSuppress}
                disabled={bulkSuppressMutation.isPending || !bulkEmails.trim()}
              >
                {bulkSuppressMutation.isPending
                  ? "Processing..."
                  : "Add to Suppression"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
